if (!customElements.get('quick-cart-drawer')) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
        this.parentElement.addEventListener('shopify:section:select', () => this.open());
        this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.toggleState = false;

      if (!this.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }

      this.querySelector('.button--close').addEventListener('click', this.close.bind(this));
      this.querySelector('.quick-cart-drawer__backdrop').addEventListener('click', this.close.bind(this));

      this.querySelector('.quick-cart-drawer__blocks').addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.toggleState) {
          this.close();
        }

        if (this.toggleState) {
          const focusableElements = this.querySelectorAll(
            'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];

          let isTabPressed = e.key === 'Tab';

          if (!isTabPressed) {
            return;
          }

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault();
              lastFocusableElement.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        }
      });

      this.initTriggers();
    }

    initTriggers() {
      this.bindUnboundTriggers();

      if (this.triggerObserver) return;

      this.triggerObserver = new MutationObserver(() => {
        this.bindUnboundTriggers();
      });

      this.triggerObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    bindUnboundTriggers() {
      document.querySelectorAll('.quick-cart-drawer__trigger').forEach(element => {
        if (element._init) return;

        element.addEventListener('click', event => {
          this.fetchProductForQuickCartDrawer(event);
        });
        element._init = true;
      });
    }

    async fetchProductForQuickCartDrawer(event) {
      event.preventDefault();

      const trigger = event.target.closest('[data-product-url]');

      if (!trigger) return;

      const isInsideDrawer = Boolean(trigger.closest('quick-cart-drawer'));
      const isActiveGroupSwatch = Boolean(trigger.closest('.product-option__label.active'));

      if (isInsideDrawer && isActiveGroupSwatch) return;

      const cardSlider = event.target.closest('card-product-slider');

      if (cardSlider) {
        cardSlider.slider.autoplay.stop();
        cardSlider.classList.add('product--open-on-quick-cart');
      }

      const isRecommendations = Boolean(trigger.classList.contains('quick-cart-drawer__trigger--recommendations'));
      const sourceProductCard = trigger.closest('product-card');

      if (!isRecommendations && !sourceProductCard) return;

      const selectedOptionValues = sourceProductCard
        ? Array.from(sourceProductCard.querySelectorAll('input[type="radio"]:checked, select')).map(
            productOption => productOption.value
          )
        : [];

      const fetchId = (this.fetchRequestId = (this.fetchRequestId || 0) + 1);
      let didRender = false;

      trigger.classList.add('is--loading');

      try {
        let fetchPrefix = '';

        if (window.Shopify.routes.root.includes(`/${window.Shopify.locale}/`)) {
          fetchPrefix = `/${window.Shopify.locale}`;
        }

        const productCardResponse = await fetch(`${fetchPrefix}${trigger.dataset.productUrl}/?view=quick-cart`);

        if (!productCardResponse.ok) return;
        if (fetchId !== this.fetchRequestId) return;

        const productCardHTML = await productCardResponse.text();

        if (fetchId !== this.fetchRequestId) return;

        const productCardWrapper = document.createElement('DIV');

        productCardWrapper.insertAdjacentHTML('beforeend', productCardHTML);

        const quickCartProduct = productCardWrapper.querySelector('.quick-cart-product');

        if (!quickCartProduct) return;

        if (this.sliderInstance?.destroy) {
          this.sliderInstance.destroy(true, true);
          this.sliderInstance = null;
        }

        const drawerMain = this.querySelector('.quick-cart-drawer__main');

        drawerMain.innerHTML = '';
        drawerMain.append(quickCartProduct);
        this.bindUnboundTriggers();
        didRender = true;

        this.sliderInstance = new Swiper('.quick-cart-drawer__media-swiper', {
          slidesPerView: 1.25,
          spaceBetween: 8,
          freeMode: {
            enabled: true
          },
          breakpoints: {
            750: {
              slidesPerView: 2
            }
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        trigger.classList.remove('is--loading');

        if (fetchId !== this.fetchRequestId || !didRender) return;

        if (!isRecommendations) {
          this.applySelectedOptionValues(selectedOptionValues);

          const quickCartProductCard = this.querySelector('product-card');

          if (quickCartProductCard) {
            quickCartProductCard.init();
            quickCartProductCard.updateFormVariantIdInput();
          }
        }

        this.bindVariantLabelUpdates();

        if (isInsideDrawer) return;

        setTimeout(() => {
          this.open();
        }, 500);
      }
    }

    applySelectedOptionValues(selectedOptionValues) {
      if (!selectedOptionValues?.length) return;

      selectedOptionValues.forEach(value => {
        const matchingInput = this.querySelector(`.variant-option-radio-input[value="${CSS.escape(value)}"]`);

        if (!matchingInput) return;

        this.querySelectorAll(`[name="${CSS.escape(matchingInput.name)}"]`).forEach(radioInput => {
          radioInput.removeAttribute('checked');
          radioInput.checked = false;
          radioInput.closest('li')?.classList.remove('checked');
        });

        matchingInput.checked = true;
        matchingInput.setAttribute('checked', '');
        matchingInput.closest('li')?.classList.add('checked');

        const selectedLabel = matchingInput.closest('fieldset')?.querySelector('[data-selected-variant]');

        if (selectedLabel) {
          selectedLabel.textContent = value;
        }
      });
    }

    bindVariantLabelUpdates() {
      this.querySelector('.quick-cart-drawer__main')
        ?.querySelectorAll('.variant-option-radio-input')
        .forEach(radioInput => {
          radioInput.addEventListener('change', event => {
            const quickCartDrawerOptionLabel = event.target.parentElement.parentElement.querySelector('legend');
            const quickCartDrawerOptionLabelSpan = quickCartDrawerOptionLabel?.querySelector('[data-selected-variant]');

            if (quickCartDrawerOptionLabelSpan) {
              quickCartDrawerOptionLabelSpan.innerHTML = event.target.value;
            }

            const selectedVariantId = this.querySelector(".product-card__add-to-cart--form input[name='id']")?.value;
            const productVariantsData = this.querySelector('product-card')?.variantsObj;
            const featuredMedia = productVariantsData?.find(variant => variant.id == selectedVariantId)?.featured_media;

            if (featuredMedia) {
              this.setActiveMedia(featuredMedia.id);
            }
          });
        });
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(media => Number(media.dataset.mediaId) === id);
      if (mediaFound) {
        this.sliderInstance.slideTo(Number(mediaFound.dataset.index));
      }
    }

    /** Drawer coreß functions */
    toggle() {
      if (!this.toggleState) {
        this.open();
      } else {
        this.close();
      }
    }

    open() {
      this.toggleState = true;
      document.querySelector('body').classList.add('overflow-hidden');
      const quickCartDrawer = document.querySelector('.quick-cart-drawer__blocks');
      const closeButton = quickCartDrawer.querySelector('.button--close');
      quickCartDrawer.setAttribute('tabindex', '0');
      closeButton.setAttribute('tabindex', '0');
      this.classList.add('is--open');
      this.opened();

      const firstFocusableElement = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }

    close() {
      this.toggleState = false;

      const shopTheLookDrawer = document.querySelector('shop-the-look-drawer');

      if (shopTheLookDrawer && !shopTheLookDrawer.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      if (!shopTheLookDrawer) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      this.classList.remove('is--open');
      this.closed();
      this.toggleAriaExpanded();

      /** start autoplay on quick cart drawe close */
      if (document.querySelector('card-product-slider.product--open-on-quick-cart')) {
        document.querySelector('card-product-slider.product--open-on-quick-cart').slider.autoplay.start();
        document.querySelector('card-product-slider.product--open-on-quick-cart').classList.remove('product--open-on-quick-cart');
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        if (event.target.closest('button')) event.target.closest('button').setAttribute('aria-expanded', true);
        this.querySelector('.button--close').setAttribute('aria-expanded', true);
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach(button => {
          button.setAttribute('aria-expanded', false);
        });
      }
    }

    opened() {
      const openedEvent = new Event('opened', { bubbles: true });
      this.dispatchEvent(openedEvent);
    }

    closed() {
      const closedEvent = new Event('closed', { bubbles: true });
      this.dispatchEvent(closedEvent);
    }
  }

  customElements.define('quick-cart-drawer', QuickCartDrawer);
}
